import tinycolor from 'tinycolor2';
import { DEFAULT_BORDER, DEFAULT_BORDER_COLOR } from '../borderStyles';
import { BOX_SHADOWS } from '../shadowStyles';

/**
 * Apparence d'un bouton — résolution unique, partagée par la box Bouton, le CTA
 * d'une slide de carrousel et la page de connexion (§1 du chantier Boutons).
 *
 * Deux props objet seulement, jamais quinze props à plat : `buttonStyle` pour
 * l'apparence, `border` pour l'arrondi et les traits (le même objet que les
 * autres box, cf. `../borderStyles`). Les anciennes props `color`, `size` et
 * `radius` restent lues : il y a du web_data réel en production et rien ne s'y
 * renomme.
 *
 * Le rendu ne lit jamais les props brutes, il passe par `resolveButtonStyle`.
 */

/** Bleu Mantine (blue-6) — couleur effective quand `color` est vide. */
export const DEFAULT_BUTTON_COLOR = '#228be6';

/**
 * Schéma de style du Bouton (cf. shared/theme/styleSchema) : ce qu'un style
 * du thème pose, et les valeurs de départ du bloc.
 *
 * `border` et `buttonStyle` n'ont pas de défaut : Puck fusionne les défauts
 * SOUS les props stockées, à chaque rendu. Les déclarer couvrirait les
 * boutons déjà en base — un bouton carré (`radius: '0'`) redeviendrait une
 * pilule. Les deux objets naissent absents, et resolveButtonStyle les
 * reconstruit depuis `color`, `size` et `radius` ; choisir un style dans la
 * galerie les pose pour de bon.
 */
export const BUTTON_STYLE_SCHEMA = {
  color: { default: DEFAULT_BUTTON_COLOR },
  size: { default: 'md' },
  radius: { default: 'xl' },
  border: {},
  buttonStyle: {},
  // Posés par un style du thème seulement : tout ce que règle l'inspecteur,
  // hors contenu (libellé, lien, icône).
  iconColor: { style: 'theme' },
  alignHorizontal: { style: 'theme', seed: true },
  alignVertical: { style: 'theme', seed: true },
  /** Style par défaut du libellé (rôle `label`). */
  textStyles: { style: 'theme' },
};

/**
 * Crans d'arrondi historiques (prop `radius`, tokens Mantine) → rem. Sert
 * uniquement de repli quand un bouton n'a pas encore d'objet `border` : c'est
 * ce qui garantit qu'une page déjà en base ne bouge pas d'un pixel.
 */
const LEGACY_RADIUS_REM = {
  // La clé numérique 0 et la chaîne '0' sont la même clé : le select stockait
  // la chaîne, un ancien enregistrement peut porter le nombre.
  0: '0', xs: '0.125rem', sm: '0.25rem', md: '0.5rem', lg: '1rem', xl: '2rem',
};

/** `hoverColor` : le survol ne change rien. */
export const HOVER_COLOR_NONE = 'none';

/** Échelle d'ombre — partagée avec les box conteneurs (`../shadowStyles`).
 *  Réexportée sous son ancien nom : elle est importée ailleurs. */
export const BUTTON_SHADOWS = BOX_SHADOWS;

/**
 * Défauts de `buttonStyle`. `pressAnimation: 'none'` est délibéré : un bouton
 * déjà en base n'a pas d'objet `buttonStyle`, et lui faire apparaître une
 * animation qu'il n'avait pas serait un changement de rendu.
 *
 * Ces défauts ne sont PAS déclarés dans les `defaultProps` du bloc : Puck les
 * fusionnerait sous les props stockées à chaque rendu, et un bouton carré déjà
 * en base redeviendrait une pilule. Les styles de la galerie, eux, posent
 * l'animation en même temps que le reste.
 */
export const DEFAULT_BUTTON_STYLE = {
  /** filled | outline | light | subtle | gradient */
  variant: 'filled',
  gradientFrom: '',
  gradientTo: '',
  gradientAngle: 135,
  /** Clé de BUTTON_SHADOWS. */
  shadow: 'none',
  /** Longueurs CSS ('' = padding de la taille Mantine choisie). */
  paddingX: '',
  paddingY: '',
  /** left | right | only */
  iconSide: 'left',
  /**
   * '' = déduite de la couleur, 'none' = aucun changement au survol, sinon la
   * couleur forcée. La sentinelle est nécessaire : sans elle, vider le champ
   * ne pouvait vouloir dire que « déduis-la », et un bouton volontairement
   * inerte au survol était impossible à décrire (même procédé que la bordure
   * `transparent` de `../borderStyles`).
   */
  hoverColor: '',
  /** none | press | ripple | lift */
  pressAnimation: 'none',
};

/** Bouton sans trait : l'arrondi se règle, aucun côté n'est affiché. */
export const BUTTON_DEFAULT_BORDER = {
  ...DEFAULT_BORDER,
  radius: '2rem',
  top: false,
  right: false,
  bottom: false,
  left: false,
};

/**
 * Couleur de survol déduite : on assombrit une couleur claire, on éclaircit une
 * couleur sombre. Un bouton bleu nuit éclairci se voit ; assombri, non.
 */
export function deriveHoverColor(hex) {
  const base = tinycolor(hex || DEFAULT_BUTTON_COLOR);
  if (!base.isValid()) return '';
  return base.isLight() ? base.darken(10).toHexString() : base.lighten(12).toHexString();
}

/** Deuxième arrêt d'un dégradé non renseigné : la couleur de base, éclaircie. */
function deriveGradientTo(hex) {
  const base = tinycolor(hex || DEFAULT_BUTTON_COLOR);
  return base.isValid() ? base.spin(20).lighten(15).toHexString() : DEFAULT_BUTTON_COLOR;
}

/**
 * L'objet `border` effectif. Sans objet stocké, il se reconstruit depuis
 * l'ancienne prop `radius` — sans trait, comme le bouton d'aujourd'hui.
 */
export function resolveButtonBorder({ border, radius } = {}) {
  if (border) return { ...BUTTON_DEFAULT_BORDER, ...border };
  const legacy = LEGACY_RADIUS_REM[radius];
  return {
    ...BUTTON_DEFAULT_BORDER,
    radius: legacy === undefined ? BUTTON_DEFAULT_BORDER.radius : legacy,
  };
}

/** Les côtés allumés, en styles React. Rien quand aucun trait n'est affiché. */
function borderSideCss(border) {
  const line = `1px solid ${border.color || DEFAULT_BORDER_COLOR}`;
  const out = {};
  ['top', 'right', 'bottom', 'left'].forEach((side) => {
    if (!border[side]) return;
    out[`border${side[0].toUpperCase()}${side.slice(1)}`] = line;
  });
  return out;
}

/**
 * Props d'un bouton → tout ce dont le rendu a besoin. Fonction pure, c'est le
 * seul point testé de l'apparence.
 *
 * @returns {{
 *   size: string, variant: string, color: string|undefined,
 *   gradient: {from: string, to: string, deg: number}|undefined,
 *   iconSide: string, pressAnimation: string,
 *   hoverColor: string, hoverFilter: string, style: Object,
 * }} Deux façons de marquer le survol : `hoverColor` remplace le fond,
 *   `hoverFilter` joue sur la luminosité — c'est la voie du dégradé, dont le
 *   fond est une image et ne s'interpole donc pas. Les deux vides = le survol
 *   ne change rien.
 */
export function resolveButtonStyle(props = {}) {
  const style = { ...DEFAULT_BUTTON_STYLE, ...(props.buttonStyle || {}) };
  const border = resolveButtonBorder(props);
  /* Couleur VIDE ≠ couleur par défaut : sans elle, Mantine applique la couleur
     primaire du thème du site, et c'est ce que rendent aujourd'hui le CTA d'une
     slide et le bouton de connexion. La forcer à DEFAULT_BUTTON_COLOR
     repeindrait ces deux boutons. */
  const color = props.color || '';
  const isGradient = style.variant === 'gradient';
  const gradient = isGradient
    ? {
      from: style.gradientFrom || color || DEFAULT_BUTTON_COLOR,
      to: style.gradientTo || deriveGradientTo(color),
      deg: Number(style.gradientAngle) || 0,
    }
    : undefined;

  const css = {
    borderRadius: border.radius || 0,
    ...borderSideCss(border),
  };

  // Une bordure explicite l'emporte sur celle que Mantine dessine pour la
  // variante « outline » : sinon les deux se superposent.
  if (color && style.variant === 'outline' && !borderHasSide(border)) {
    css.borderColor = color;
  }

  const shadow = BUTTON_SHADOWS[style.shadow] || '';
  if (shadow) css.boxShadow = shadow;
  if (style.paddingX) css.paddingInline = style.paddingX;
  if (style.paddingY) {
    css.paddingBlock = style.paddingY;
    // Mantine fige la hauteur par taille ; sans ça le padding vertical n'a
    // aucun effet visible.
    css.height = 'auto';
  }

  const inert = style.hoverColor === HOVER_COLOR_NONE;
  const hoverColor = inert ? '' : style.hoverColor || derivedHoverBackground(style.variant, color);
  // Une couleur forcée gagne sur la luminosité déduite : c'est un choix
  // explicite, et cumuler les deux assombrirait deux fois.
  const hoverFilter = inert || style.hoverColor ? '' : derivedHoverFilter(gradient);
  if (hoverFilter) css['--btn-hover-filter'] = hoverFilter;
  if (hoverColor) css['--btn-hover-bg'] = hoverColor;
  if (shadow || style.pressAnimation === 'lift') {
    css['--btn-hover-shadow'] = BUTTON_SHADOWS[bumpShadow(style.shadow)];
  }

  return {
    size: props.size || 'md',
    variant: style.variant,
    color: isGradient || !color ? undefined : color,
    gradient,
    iconSide: style.iconSide,
    pressAnimation: style.pressAnimation,
    hoverColor,
    hoverFilter,
    style: css,
  };
}

/**
 * Le fond du survol quand il n'est pas forcé.
 *
 * Rien n'est dérivé sur « Contour », « Doux » et « Discret » : y poser un aplat
 * au survol en ferait un tout autre bouton, et Mantine y fait déjà ce qu'il
 * faut. Sans couleur choisie non plus : Mantine gère le survol de sa couleur
 * primaire mieux que nous ne le devinerions. Le dégradé, lui, passe par
 * `derivedHoverFilter` — voir ci-dessous.
 */
function derivedHoverBackground(variant, color) {
  return color && variant === 'filled' ? deriveHoverColor(color) : '';
}

/**
 * Survol d'un bouton dégradé, en luminosité plutôt qu'en fond.
 *
 * Mantine renvoie, pour cette variante, un survol identique au fond
 * (`hover: getGradient(...)`) : sans intervention, un bouton dégradé n'a aucun
 * retour au survol. Redessiner le dégradé avec des arrêts assombris marchait,
 * mais basculait d'un coup — une image de fond ne s'interpole pas en CSS.
 * `filter`, si. Le sens de la variation suit la même règle que
 * `deriveHoverColor`, décidée sur le mélange des deux arrêts : on assombrit un
 * dégradé clair, on éclaircit un dégradé sombre.
 */
function derivedHoverFilter(gradient) {
  if (!gradient) return '';
  const mixed = tinycolor.mix(gradient.from, gradient.to, 50);
  return mixed.isLight() ? 'brightness(0.9)' : 'brightness(1.15)';
}

function borderHasSide(border) {
  return !!(border.top || border.right || border.bottom || border.left);
}

/** L'ombre d'un cran au-dessus — ce que le survol affiche. */
function bumpShadow(key) {
  const order = ['none', 'sm', 'md', 'lg'];
  const i = order.indexOf(key);
  return order[Math.min(order.length - 1, (i < 0 ? 0 : i) + 1)];
}

/* ------------------------------------------------------------------ styles */

const preset = (key, label, buttonStyle, border) => ({
  key,
  label,
  props: {
    buttonStyle: { ...DEFAULT_BUTTON_STYLE, pressAnimation: 'press', ...buttonStyle },
    border: { ...BUTTON_DEFAULT_BORDER, ...border },
  },
});

/**
 * Un style est un JEU DE VALEURS, pas une variante : rien n'est persisté qui
 * dirait lequel est actif, le style courant se DÉDUIT des props et un bouton
 * réglé à la main retombe sur « Personnalisé » (doctrine des chantiers 4, 6 et
 * 9).
 *
 * Aucun style ne touche à `color` : c'est le choix de marque de l'utilisateur,
 * changer de style ne doit pas l'effacer.
 */
export const BUTTON_STYLE_PRESETS = [
  preset('filled', 'Plein', {}, { radius: '0.5rem' }),
  preset('pill', 'Pilule', {}, { radius: '2rem' }),
  preset('outline', 'Contour', { variant: 'outline' }, { radius: '0.5rem' }),
  preset('light', 'Doux', { variant: 'light' }, { radius: '0.5rem' }),
  preset('gradient', 'Dégradé', { variant: 'gradient' }, { radius: '0.5rem' }),
  preset('subtle', 'Discret', { variant: 'subtle' }, { radius: '0.5rem' }),
  preset('elevated', 'Relief', { shadow: 'md', pressAnimation: 'lift' }, { radius: '0.5rem' }),
];

/**
 * Le patch qu'un style écrit. L'animation et le côté de l'icône déjà réglés
 * sont conservés — le style ne les prescrit pas : ce sont des réglages
 * orthogonaux.
 * Les objets sont recopiés : chaque bouton doit avoir les siens.
 */
export function buttonStylePatch(def, current = {}) {
  const kept = current.buttonStyle || {};
  return {
    buttonStyle: {
      ...def.props.buttonStyle,
      pressAnimation: kept.pressAnimation ?? def.props.buttonStyle.pressAnimation,
      iconSide: kept.iconSide ?? def.props.buttonStyle.iconSide,
    },
    border: { ...def.props.border },
  };
}
